/**
 * ShopDiscountsView - Discount codes management for Shop administration
 * Extracted from AdminShopTab.js in Phase 4D-6
 * 
 * Displays:
 * - List of discount codes in table format
 * - Code, discount value (% or £), min order, uses, expires, status
 * - Delete action for each code
 * - Empty state when no codes exist
 * 
 * Note: No edit functionality exists in the original implementation
 * Note: No enable/disable toggle exists (only set on creation)
 */
import React from 'react';
import { Plus, Tag, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';

/**
 * Format discount value based on type
 * @param {string} type - 'percentage' or 'fixed'
 * @param {number} value - Discount value
 * @returns {string} Formatted discount (e.g., "20%" or "£5.00")
 */
function formatDiscountValue(type, value) {
  if (type === 'percentage') {
    return `${value}%`;
  }
  return `£${value.toFixed(2)}`;
}

/**
 * Format expiry date for display
 * @param {string} dateStr - ISO date string or null
 * @returns {string} Formatted date or "Never"
 */
function formatExpiryDate(dateStr) {
  if (!dateStr) return 'Never';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB');
  } catch {
    return 'Never';
  }
}

/**
 * @param {Object} props
 * @param {Array} props.discountCodes - Array of discount code objects
 * @param {string} props.discountCodes[].code - The discount code
 * @param {string} props.discountCodes[].discount_type - 'percentage' or 'fixed'
 * @param {number} props.discountCodes[].discount_value - Discount value
 * @param {number|null} props.discountCodes[].min_order_amount - Minimum order amount
 * @param {number} props.discountCodes[].uses_count - Number of times used
 * @param {number|null} props.discountCodes[].max_uses - Maximum allowed uses
 * @param {string|null} props.discountCodes[].expires_at - Expiry date (ISO string)
 * @param {boolean} props.discountCodes[].is_active - Whether code is active
 * @param {Function} props.onCreateClick - Callback when Create Discount button clicked
 * @param {Function} props.onDeleteDiscount - Callback when delete button clicked (receives code)
 */
export function ShopDiscountsView({
  discountCodes,
  onCreateClick,
  onDeleteDiscount
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <CardTitle>Discount Codes</CardTitle>
            <CardDescription>Create and manage promotional discount codes</CardDescription>
          </div>
          <Button onClick={onCreateClick} data-testid="add-discount-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Discount
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {discountCodes.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Tag className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p>No discount codes yet</p>
            <p className="text-sm">Create your first discount code to offer promotions</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Min Order</TableHead>
                <TableHead>Uses</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {discountCodes.map((code) => (
                <TableRow key={code.code}>
                  <TableCell className="font-mono font-bold">{code.code}</TableCell>
                  <TableCell>
                    {formatDiscountValue(code.discount_type, code.discount_value)}
                  </TableCell>
                  <TableCell>
                    {code.min_order_amount ? `£${code.min_order_amount.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell>
                    {code.uses_count || 0}
                    {code.max_uses && ` / ${code.max_uses}`}
                  </TableCell>
                  <TableCell>
                    {formatExpiryDate(code.expires_at)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={code.is_active ? 'default' : 'secondary'}>
                      {code.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteDiscount(code.code)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default ShopDiscountsView;
